import { useEffect, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DictErrorCode, useTostify } from '@sprytki-frontend-apps/common';
import { observer } from 'mobx-react-lite';
import { LockerServiceAPI } from 'api/services/lockerServiceAPI';
import { alertStore } from 'stores/alertStore/alertStore';
import { appStore } from 'stores/appStore';
import { locationStore } from 'stores/locationStore/locationStore';
import { StyledDialogContent } from 'components/alerts/StyledComponents';
import { DualTitleDialog } from '../DualTitleDialog';

export type CodeKind = 'access' | 'emergency';

interface EditCodeDialogProps {
	open: boolean;
	onClose: () => void;
	lockerId: string;
	userFriendlyId: string;
	typeLabel: string;
	codeKind: CodeKind;
	// Passed in rather than rebuilt here, so the field label always matches the row that opened it
	// (it varies with the location's code length and with weekend codes).
	codeLabel: string;
	currentCode: string;
}

const EditCodeDialogComponent = ({
	open,
	onClose,
	lockerId,
	userFriendlyId,
	typeLabel,
	codeKind,
	codeLabel,
	currentCode,
}: EditCodeDialogProps) => {
	const { t } = useTranslation();
	const toast = useTostify();
	const [code, setCode] = useState(currentCode ?? '');

	useEffect(() => {
		if (open) setCode(currentCode ?? '');
	}, [open, currentCode]);

	// Same rules as the Admin Panel: the location decides how many digits a code has, and the suffix is
	// only what the keypad expects after them — it is never part of the stored value.
	const accessCodeLength = locationStore.selectedLocation?.accessCodeLength ?? 0;
	const accessCodeSuffix = locationStore.selectedLocation?.accessCodeSuffix ?? '';
	const hasLengthRule = accessCodeLength > 0;

	const isValidFormat = hasLengthRule ? code.length === accessCodeLength : code.length > 0;
	const isChanged = code !== (currentCode ?? '');
	const canSave = isValidFormat && isChanged && !appStore.isLoading;
	const showFormatError = code.length > 0 && !isValidFormat;

	const handleChange = (value: string) => {
		const digitsOnly = value.replace(/\D/g, '');
		setCode(hasLengthRule ? digitsOnly.slice(0, accessCodeLength) : digitsOnly);
	};

	const handleSave = async () => {
		if (!canSave) return;

		appStore.setIsLoading(true);
		try {
			const response =
				codeKind === 'emergency'
					? await LockerServiceAPI.setEmergencyCode(lockerId, code)
					: await LockerServiceAPI.setAccessCode(lockerId, code);

			if (!response.success) {
				toast(t(`errorCodes.${response.errors?.[0]?.errorCode ?? DictErrorCode.InternalError}`), 'error');
				return;
			}

			if (codeKind === 'emergency') {
				alertStore.updateEmergencyCode(lockerId, code);
			} else {
				alertStore.updateAccessCode(lockerId, code);
			}

			toast(t('common.changesSaved'), 'success');
			onClose();
		} catch (error: any) {
			const errorCode = error?.response?.data?.errors?.[0]?.errorCode ?? DictErrorCode.InternalError;
			toast(
				t(`errorCodes.${errorCode}`, {
					defaultValue: t(`errorCodes.${DictErrorCode.InternalError}`),
				})
			);
		} finally {
			appStore.setIsLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={() => !appStore.isLoading && onClose()} fullWidth maxWidth='xs'>
			<DualTitleDialog
				dialogTitle={
					codeKind === 'emergency'
						? t('pages.alerts.itemDetails.editEmergencyAccessCode')
						: t('pages.alerts.itemDetails.editAccessCode')
				}
				dialogTitleSecondary={`${typeLabel}: ${userFriendlyId}`}
			/>

			<StyledDialogContent>
				<TextField
					autoFocus
					fullWidth
					variant='standard'
					margin='dense'
					type='tel'
					label={codeLabel}
					value={code}
					onChange={(e) => handleChange(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter') handleSave();
					}}
					error={showFormatError}
					helperText={
						hasLengthRule
							? t('pages.alerts.itemDetails.accessCodeHint', { accessCodeLength })
							: undefined
					}
					disabled={appStore.isLoading}
					slotProps={{
						inputLabel: { shrink: true },
						htmlInput: {
							inputMode: 'numeric',
							maxLength: hasLengthRule ? accessCodeLength : undefined,
							style: { textAlign: 'right', letterSpacing: '0.3em' },
						},
						input: {
							endAdornment: accessCodeSuffix ? (
								<InputAdornment position='end' sx={{ mr: 0 }}>
									<strong>{accessCodeSuffix}</strong>
								</InputAdornment>
							) : undefined,
						},
					}}
				/>
			</StyledDialogContent>

			<DialogActions>
				<Button variant='outlined' onClick={onClose} disabled={appStore.isLoading}>
					{t('common.cancel')}
				</Button>
				<Button
					variant='contained'
					color='primary'
					onClick={handleSave}
					disabled={!canSave}
					sx={{ position: 'relative' }}
				>
					<span style={{ visibility: appStore.isLoading ? 'hidden' : 'visible' }}>{t('common.save')}</span>
					{appStore.isLoading && <CircularProgress color='inherit' size={20} sx={{ position: 'absolute' }} />}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export const EditCodeDialog = observer(EditCodeDialogComponent);
